import { track } from "@amplitude/analytics-react-native";
import { DragEvent, ReactElement, useCallback } from "react";
import { View } from "react-native";
import { amp } from "src/api/amplitude";
import parseFileToAsset from "src/api/asset-persist/helpers/parseFileToAsset.web";
import queueUpload from "src/api/asset-persist/queueUpload";
import ValidFileUploadTypes from "src/constants/validFileUploadTypes";
import { useGatheringStore } from "src/contexts/GatheringContext";
import getFilteredUploadFileTypes from "src/helpers/fileHelpers/getFilteredUploadFileTypes";
import getRandomString from "src/helpers/getRandomString";
import { getClientId } from "src/helpers/globalHelpers";
import toastUnsupportedFileTypes from "src/helpers/toastUnsupportedFileTypes";
import useDim from "src/hooks/useDim";
import useUploadAgreement from "../UploadPhotosPrompt/hooks/useUploadAgreement";

const FileUploadAreaWrapper = ({ children }: { children: ReactElement | ReactElement[] }) => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const numPhotos = useGatheringStore((state) => state.numPhotos);
	const ownerId = useGatheringStore((state) => state.ownerId);
	const isHoveringImageFile = useGatheringStore((state) => state.isHoveringImageFile);
	const setIsHoveringImageFile = useGatheringStore((state) => state.setIsHoveringImageFile);
	const { checkUploadAgreement } = useUploadAgreement();

	const handleDrop = useCallback(
		async (event: DragEvent<HTMLDivElement>) => {
			event.preventDefault();
			setIsHoveringImageFile(false);
			const { validFiles, numUnsupportedFiles, unsupportedFileTypes } =
				getFilteredUploadFileTypes(event.dataTransfer.files);
			if (numUnsupportedFiles > 0) {
				toastUnsupportedFileTypes({
					numUnsupportedFiles,
					unsupportedFileTypes,
				});
			}
			if (validFiles.length === 0) return;

			const { shouldContinue, onUpload } = await checkUploadAgreement();
			if (!shouldContinue) return;

			track(amp.photo.uploadStart, {
				method: "dragAndDrop",
				dataLength: validFiles.length,
			});

			const localSelectionId = getRandomString();
			await queueUpload(
				validFiles.map((file, i) => ({
					...parseFileToAsset(file, {
						localSelectionId,
						localSelectionIndex: i,
					}),
					gatheringId: gatheringId,
					isThumbnail: numPhotos === 0 && i === 0 && getClientId() === ownerId,
				})),
			);
			onUpload?.();
		},
		[checkUploadAgreement, gatheringId, numPhotos, ownerId, setIsHoveringImageFile],
	);
	const dim = useDim();

	return (
		<div
			onDrop={handleDrop}
			onDragOver={(event) => {
				event.preventDefault();
			}}
			onDragEnter={(event) => {
				event.preventDefault();
				if (
					!isHoveringImageFile &&
					event.dataTransfer.items.length > 0 &&
					Array.from(event.dataTransfer.items).some(
						(i) => i.kind === "file" && ValidFileUploadTypes.has(i.type),
					)
				) {
					setIsHoveringImageFile(true);
				}
			}}
			onDragEnd={(event) => {
				event.preventDefault();
			}}
			onDragExit={(event) => {
				event.preventDefault();
				setIsHoveringImageFile(false);
			}}
			onMouseOut={() => {
				if (isHoveringImageFile) setIsHoveringImageFile(false);
			}}
			style={{
				width: dim.width,
				height: dim.height,
			}}
		>
			<View
				style={{
					width: dim.width,
					height: dim.height,
				}}
			>
				{children}
			</View>
		</div>
	);
};
export default FileUploadAreaWrapper;
